import useAiChatPanel from '@/composables/ai-chat/ai-chat-panel';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePersonalAppSettings from '@/composables/logged-in-user/personal-app-settings';
import { layoutWidth as aiChatLayoutWidth, constrainWidth as constrainAiChatWidth } from '@/lib/ai-chat-panel';
import { bothPanelsFit, fitPanelWidths, panel } from '@/lib/side-panel-layout';
import { computed, ref, watch } from 'vue';
import { constrainWidth as constrainSidePeekWidth, layoutWidth as sidePeekLayoutWidth } from '@/lib/side-peek';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { goalViewMode as viewMode } from 'shared/constants.json';

const constrainWidth = {
  [panel.sidePeek]: constrainSidePeekWidth,
  [panel.aiChat]: constrainAiChatWidth,
};

const openOrder = ref([]);

const windowWidth = ref(window.innerWidth);

window.addEventListener('resize', () => { windowWidth.value = window.innerWidth; });

export const resetOpenOrder = () => { openOrder.value = []; };

export default function useSidePanels() {
  const store = useStore();
  const route = useRoute();
  const { loggedInUser } = useLoggedInUser();
  const { personalAppSettings } = usePersonalAppSettings(loggedInUser);
  const { open: aiChatOpen, width: aiChatWidth } = useAiChatPanel();

  const smAndDown = computed(() => store.state.breakpoint.smAndDown);
  const goalDetailsOpen = computed(() => route.query.goalId !== undefined);

  const openPanels = computed(() => ({
    [panel.sidePeek]: goalDetailsOpen.value,
    [panel.aiChat]: aiChatOpen.value,
  }));

  watch(openPanels, (isOpen) => {
    const stillOpen = openOrder.value.filter((name) => isOpen[name]);
    const opened = Object.values(panel).filter((name) => isOpen[name] && !stillOpen.includes(name));
    openOrder.value = [...stillOpen, ...opened];
  }, { immediate: true });

  const topPanel = computed(() => openOrder.value[openOrder.value.length - 1] ?? null);

  const sidePeekOpen = computed(() => goalDetailsOpen.value
    && personalAppSettings.value?.goalViewMode !== viewMode.centerPeek);

  const requestedWidth = computed(() => ({
    [panel.sidePeek]: sidePeekLayoutWidth({
      open: sidePeekOpen.value,
      width: personalAppSettings.value?.goalSidePeekWidth,
      windowWidth: windowWidth.value,
    }),
    [panel.aiChat]: aiChatLayoutWidth({
      open: aiChatOpen.value,
      smAndDown: smAndDown.value,
      width: aiChatWidth.value,
      windowWidth: windowWidth.value,
    }),
  }));

  const requested = (name, override) => (override === undefined
    ? requestedWidth.value[name]
    : constrainWidth[name](override, windowWidth.value));

  const fitWidths = ({ sidePeek, aiChat, dragged = null } = {}) => fitPanelWidths({
    windowWidth: windowWidth.value,
    sidePeek: requested(panel.sidePeek, sidePeek),
    aiChat: requested(panel.aiChat, aiChat),
    dragged,
  });

  // a safety net rather than a live path: the two minimums still fit the narrowest window
  // that keeps these side panels instead of fullscreen dialogs, so one of them has to grow
  // before both can be open with no room for either
  const panelToClose = computed(() => {
    const { sidePeek, aiChat } = requestedWidth.value;
    if (sidePeek === 0 || aiChat === 0 || bothPanelsFit(windowWidth.value)) {
      return null;
    }
    return topPanel.value === panel.aiChat ? panel.sidePeek : panel.aiChat;
  });

  return {
    windowWidth,
    sidePeekOpen,
    fittedWidth: computed(() => fitWidths()),
    fitWidths,
    isOnTop: (name) => topPanel.value === name,
    panelToClose,
  };
}
